<template>
  <component :is="hasCaptionText ? 'figure' : 'div'">
    <picture>
      <template v-if="isCarousel">
        <source
          v-for="(width, key, index) in breakpoints"
          :key="index"
          :data-srcset="srcset[index]"
          :class="[cssClass, computedClasses]"
          :media="`(max-width: ${width}px)`"
        />
      </template>
      <img
        :id="elementId"
        :class="[cssClass, computedClasses]"
        :data-srcset="srcset.join(',')"
        :data-src="getSrc"
        :alt="altText"
        :data-fit-object="isDataObjectFit"
        :data-asset-id="trimmedAssetId"
        :data-sizes="sizes"
        :srcset="srcset.join(',')"
        @load="$emit('loaded')"
      />
    </picture>
    <figcaption v-if="hasCaptionText">
      {{ captionText }}
    </figcaption>
  </component>
</template>

<script>
import {Cloudinary, CloudinaryConstants} from '@/utils/image/cloudinary-helpers';

export default {
  name: 'ImageComponent',
  props: {
    /**
     * Sets up the component to be used as a carousel
     */
    isCarousel: {
      type: Boolean,
      default: false,
    },
    /**
     * Adds classes to object fit image and center to container.
     */
    isDataObjectFit: {
      type: Boolean,
      default: false,
    },
    /**
     * Adds id to img.
     */
    elementId: {
      type: String,
      default: '',
    },
    /**
     * Adds alt text.
     */
    altText: {
      type: String,
      default: '',
    },
    /**
     * Full url path.
     */
    src: {
      type: String,
      default: '',
    },
    sizes: {
      type: String,
      default: '',
    },
    /**
     * Add additional classes to the img element
     */
    cssClass: {
      type: [Object, String, Array],
      default: () => '',
    },
    /**
     * Adjusts Cloudinary settings for image
     */
    transformation: {
      type: String,
      default: '',
      validator: (val) => ['', 'pad', 'fill', 'limit', 'scale', 'auto'].includes(val),
    },
    /**
     * Add Cloudinary effect to image.
     */
    effect: {
      type: String,
      default: '',
      validator: (val) => ['', 'make_transparent'].includes(val),
    },
    /**
     * Simple asset id in our media library. Not full url.
     */
    assetId: {
      type: String,
      default: '',
    },
    /**
     * Sets the aspect ratio, 16x9, 4x3.
     */
    aspectRatio: {
      type: String,
      default: '',
    },
    /**
     * Sets a custom breakpoint map
     */
    breakpointMap: {
      type: Object,
      default: () => ({}),
    },
    captionText: {
      type: String,
      default: '',
    },
  },
  emits: ['loaded'],
  data() {
    return {
      breakpoints: {
        xsWidth: 480,
        smWidth: 640,
        mdWidth: 768,
        mlWidth: 1024,
        lgWidth: 1200,
        xlWidth: 1440,
        xxlWidth: 1920,
      },
    };
  },
  computed: {
    computedClasses() {
      return {
        lazyload: !this.isCarousel,
        'owl-lazy': this.isCarousel,
        'object-center object-cover': this.isDataObjectFit,
      };
    },
    srcset() {
      return Object.entries(this.breakpoints).map(([key, breakpointWidth]) => {
        const width = this.breakpointMap?.[key] ?? breakpointWidth;
        return this.getImageUrl(width);
      });
    },
    calculatedAspectRatio() {
      return this.aspectRatio ? this.aspectRatio.split('x')[1] / this.aspectRatio.split('x')[0] : undefined;
    },
    trimmedAssetId() {
      //check for full URL vs assetId
      const urlRegex = new RegExp(
        /(http(s)?:\/\/.)?(\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.,~#?&//=]*)/
      );
      if (!urlRegex.test(this.assetId)) return this.assetId;
      return this.trimUrl(this.assetId);
    },
    getSrc() {
      return this.getImageUrl(this.breakpoints.xxlWidth);
    },
    hasCaptionText() {
      return !!this.captionText;
    },
  },
  methods: {
    getImageUrl(width) {
      if (!this.trimmedAssetId) return;
      const height = this.calculatedAspectRatio ? Math.floor(width * this.calculatedAspectRatio) : undefined;
      const transformations = {
        w: width,
        ...(height && {h: height}),
        ...(this.transformation && {c: this.transformation}),
        ...(this.effect && {e: this.effect}),
        ...CloudinaryConstants.DEFAULT_PARAMETERS,
      };

      return `${Cloudinary.createAssetUrl(this.trimmedAssetId, transformations)} ${width}w`;
    },
    trimUrl(url) {
      //this is a hack to get around the fact that the assetId might be a full url.
      //Cloudinary urls follow the format /image/upload/<transformations>/<assetId>
      //however the transformations are optional.
      let trimmedAssetId = url.split('/');
      return trimmedAssetId[trimmedAssetId.length - 1];
    },
  },
};
</script>
